import './friends.css';

import { type BlockedPlayer, type Friend, type FriendsState, getZone } from '@shefira/shared';

import { type DomAtlas, loadCharacterAtlas } from '../assets/dom-atlas.js';
import { isAction } from '../settings/keybindings.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { headPortrait } from './portrait.js';
import { uiFormat, uiText } from './text.js';
import { kitTitle } from './kit.js';

export interface FriendsPanelOptions {
  onAdd: (name: string) => void;
  onMessage: (name: string) => void;
  onInvite: (name: string) => void;
  onRemove: (characterId: string) => void;
  /** Stop blocking a player (ADR 0076), by name. */
  onUnblock: (name: string) => void;
}

/**
 * The friends' list (ADR 0033): a HUD button with the count of friends
 * online, and a window listing them — online first, with their head as they
 * are dressed and where they play —
 * to write to one, invite them in the party, or end the friendship. Only a
 * view of what the server sent in `friends`; every button is an intention.
 */
export class FriendsPanel {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly count: HTMLElement;
  private readonly badge: HTMLElement;
  private readonly button: HTMLButtonElement;
  private friends: Friend[] = [];
  /** Players blocked (ADR 0076), listed under the friends to be unblocked. */
  private blocked: readonly BlockedPlayer[] = [];
  private atlas: DomAtlas | null = null;
  /** The friend whose « Retirer » was clicked once: a second click confirms. */
  private removing?: string;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (event.key === 'Escape' && !this.root.hidden) {
      this.close();
      return;
    }
    if (!isAction(event, 'friends')) return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly options: FriendsPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'friends';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Amis'));

    const title = document.createElement('h2');
    title.className = 'friends__title';
    kitTitle(title, 'friends');
    this.count = document.createElement('span');
    title.append(this.count);
    title.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    const form = document.createElement('form');
    form.className = 'friends__add';
    const input = document.createElement('input');
    input.className = 'friends__input';
    input.maxLength = 16;
    input.placeholder = uiText('Nom du joueur');
    input.setAttribute('aria-label', uiText('Nom du joueur à ajouter'));
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') input.blur();
      event.stopPropagation();
    });
    const add = document.createElement('button');
    add.type = 'submit';
    add.className = 'friends__button';
    add.textContent = uiText('Ajouter');
    form.append(input, add);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const name = input.value.trim();
      if (name.length === 0) return;
      input.value = '';
      this.options.onAdd(name);
    });

    this.list = document.createElement('ul');
    this.list.className = 'friends__list';
    this.root.append(title, form, this.list);
    document.body.append(this.root);

    this.button = hudShortcutButton('friends', 'Amis', 'friends');
    this.badge = document.createElement('span');
    this.badge.className = 'friends__badge';
    this.badge.hidden = true;
    this.button.append(this.badge);
    this.button.addEventListener('click', () => {
      this.toggle();
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
    void loadCharacterAtlas().then((atlas) => {
      this.atlas = atlas;
      this.render();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    if (this.root.hidden) this.open();
    else this.close();
  }

  open(): void {
    this.root.hidden = false;
    this.removing = undefined;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  setFriends(state: FriendsState): void {
    this.friends = state.friends;
    this.render();
  }

  setBlocked(blocked: readonly BlockedPlayer[]): void {
    this.blocked = blocked;
    this.render();
  }

  private render(): void {
    const online = this.friends.filter((friend) => friend.online).length;
    this.count.textContent = uiFormat('Amis ({online} en ligne sur {total})', {
      online,
      total: this.friends.length,
    });
    this.badge.hidden = online === 0;
    this.badge.textContent = String(online);

    this.list.replaceChildren();
    if (this.friends.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'friends__empty';
      empty.textContent = uiText(
        "Pas encore d'amis. Ajoute un joueur par son nom, ou clique sur lui dans le monde.",
      );
      this.list.append(empty);
    }
    for (const friend of this.friends) this.list.append(this.row(friend));
    if (this.blocked.length === 0) return;
    const heading = document.createElement('li');
    heading.className = 'friends__section';
    heading.textContent = uiFormat('Joueurs bloqués ({count})', { count: this.blocked.length });
    this.list.append(heading);
    for (const player of this.blocked) this.list.append(this.blockedRow(player));
  }

  private blockedRow(player: BlockedPlayer): HTMLLIElement {
    const row = document.createElement('li');
    row.className = 'friends__row friends__row--blocked';
    const who = document.createElement('div');
    who.className = 'friends__who';
    const name = document.createElement('span');
    name.className = 'friends__name';
    name.textContent = uiText(player.name);
    who.append(name);
    const actions = document.createElement('div');
    actions.className = 'friends__actions';
    const unblock = document.createElement('button');
    unblock.type = 'button';
    unblock.className = 'friends__button';
    unblock.textContent = uiText('Débloquer');
    unblock.addEventListener('click', () => {
      this.options.onUnblock(player.name);
    });
    actions.append(unblock);
    row.append(who, actions);
    return row;
  }

  private row(friend: Friend): HTMLLIElement {
    const row = document.createElement('li');
    row.className = 'friends__row';
    row.dataset.online = String(friend.online);
    row.dataset.away = String(friend.online && friend.away);

    const who = document.createElement('div');
    who.className = 'friends__who';
    const name = document.createElement('span');
    name.className = 'friends__name';
    name.textContent = uiText(friend.name);
    const where = document.createElement('span');
    where.className = 'friends__where';
    where.textContent = whereabouts(friend);
    who.append(name, where);

    const actions = document.createElement('div');
    actions.className = 'friends__actions';
    const action = (label: string, run: () => void): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'friends__button';
      button.textContent = uiText(label);
      button.addEventListener('click', run);
      actions.append(button);
      return button;
    };
    if (friend.online) {
      action('Message', () => {
        this.close();
        this.options.onMessage(friend.name);
      });
      action('Inviter', () => {
        this.options.onInvite(friend.name);
      });
    }
    const confirming = this.removing === friend.id;
    const remove = action(confirming ? 'Sûr ?' : 'Retirer', () => {
      if (this.removing === friend.id) {
        this.removing = undefined;
        this.options.onRemove(friend.id);
      } else {
        this.removing = friend.id;
        this.render();
      }
    });
    remove.classList.add('friends__button--remove');

    // Away, a friend is an empty slot: the server only knows how players in the game look.
    const slot = document.createElement('div');
    slot.className = 'friends__portrait';
    if (friend.portrait !== null && this.atlas !== null) {
      slot.append(headPortrait(this.atlas, friend.portrait));
    }
    row.append(slot, who, actions);
    return row;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
    this.button.remove();
  }
}

/** Where they play, or « Absent · Havrebourg » while away from the keyboard (ADR 0080). */
function whereabouts(who: { online: boolean; away: boolean; zoneId: string | null }): string {
  if (!who.online) return uiText('Hors ligne');
  const place = uiText(getZone(who.zoneId ?? '')?.name ?? 'En ligne');
  return who.away ? uiFormat('Absent · {place}', { place }) : place;
}
